/**
 * IndustryLatestYearCapitalChart
 *
 * Grouped bar chart showing ROIC and WACC for each company (latest year),
 * with industry median reference lines. Clean side-by-side comparison.
 */
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Card, CardHeader, CardBody } from '../../ui/Card';
import type { CompanyFinancialSummary, IndustryBenchmarkRecord } from '../../../types/industryResearch';

interface Props {
  companies: CompanyFinancialSummary[];
  latestMedian: IndustryBenchmarkRecord;
}

export default function IndustryLatestYearCapitalChart({ companies, latestMedian }: Props) {
  const withData = companies.filter((c) => c.has_financial_data && (c.latest_roic != null || c.latest_wacc != null));

  if (withData.length === 0) return null;

  // Sort by ROIC descending
  const sorted = [...withData].sort((a, b) => (b.latest_roic ?? 0) - (a.latest_roic ?? 0));

  const chartData = sorted.map((co) => ({
    name: co.ticker ?? co.company_name.split(' ')[0],
    fullName: co.company_name,
    roic: co.latest_roic != null ? parseFloat(co.latest_roic.toFixed(1)) : null,
    wacc: co.latest_wacc != null ? parseFloat(co.latest_wacc.toFixed(1)) : null,
  }));

  const medianRoic = latestMedian.roic != null
    ? parseFloat((latestMedian.roic * 100).toFixed(1))
    : null;
  const medianWacc = latestMedian.wacc != null
    ? parseFloat((latestMedian.wacc * 100).toFixed(1))
    : null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-neutral-800">ROIC vs WACC by Company (%)</h3>
            <p className="text-xs text-neutral-500 mt-0.5">Latest year · dashed lines = industry medians</p>
          </div>
          <div className="flex items-center gap-3 text-xs">
            {medianRoic != null && (
              <span className="flex items-center gap-1 text-emerald-700 font-medium">
                <span className="inline-block w-4 border-t-2 border-dashed border-emerald-600" />
                Median ROIC {medianRoic}%
              </span>
            )}
            {medianWacc != null && (
              <span className="flex items-center gap-1 text-rose-700 font-medium">
                <span className="inline-block w-4 border-t-2 border-dashed border-rose-500" />
                Median WACC {medianWacc}%
              </span>
            )}
          </div>
        </div>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} margin={{ top: 16, right: 16, left: 8, bottom: 0 }} barCategoryGap="25%">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis width={44} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} domain={['auto', 'auto']} />
            {medianRoic != null && (
              <ReferenceLine y={medianRoic} stroke="#10b981" strokeDasharray="5 3" strokeWidth={1.5} />
            )}
            {medianWacc != null && (
              <ReferenceLine y={medianWacc} stroke="#ef4444" strokeDasharray="5 3" strokeWidth={1.5} />
            )}
            <Tooltip
              formatter={(v: number, name: string) => [`${v?.toFixed(1)}%`, name.toUpperCase()]}
              labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName ?? label}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="roic" name="ROIC" fill="#10b981" radius={[4, 4, 0, 0]}>
              {chartData.map((entry) => (
                <Cell
                  key={entry.name}
                  fill={
                    entry.roic != null && medianRoic != null && entry.roic >= medianRoic
                      ? '#10b981'
                      : '#6ee7b7'
                  }
                />
              ))}
            </Bar>
            <Bar dataKey="wacc" name="WACC" fill="#fca5a5" radius={[4, 4, 0, 0]}>
              {chartData.map((entry) => (
                <Cell key={entry.name} fill="#fca5a5" />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  );
}
